import { toTitleCase } from '@xemplo/common-util';

import {
  Code,
  CountryInfo,
  CountryName,
  Currency,
  CurrencySymbol,
  Locale,
  PhoneCode,
  PhoneCodeRegionalCode,
} from './country.types';

/**
 * Reverse lookup map: locale string (e.g. 'en-AU') → country name (e.g. 'Australia').
 * Used for efficient O(1) locale-to-country mapping.
 *
 * @see {@link Locale}
 */
const localeToCountry = Object.entries(Locale).reduce(
  (acc, [country, locale]) => {
    acc[locale] = country as keyof typeof Locale;
    return acc;
  },
  {} as Record<string, keyof typeof Locale>,
);

/**
 * Reverse lookup map: code string (e.g. 'AU') → country name (e.g. 'Australia').
 * Used for efficient O(1) code-to-country mapping.
 *
 * @see {@link Code}
 */
const codeToCountry = Object.entries(Code).reduce(
  (acc, [country, code]) => {
    acc[code] = country as keyof typeof Code;
    return acc;
  },
  {} as Record<string, keyof typeof Code>,
);

/**
 * Normalizes UK to GB for consistent handling.
 * UK is used internally/in DB, GB is ISO 3166-1 alpha-2 standard.
 * Both codes refer to the same country data.
 *
 * @param code - Country code to normalize
 * @returns GB if code is UK, otherwise returns the code unchanged
 *
 * @see {@link Code}
 */
function normalizeUKtoGB(code: Code): Code {
  return code === Code.United_Kingdom_UK_Alias ? Code.United_Kingdom : code;
}

/**
 * Countries whose display names cannot be derived safely from `toTitleCase`.
 * Stored as a Map so we can use `.has()` and `.get()` cleanly.
 * NOTE: In order to avoid the hardcoded country names, we would need to
 * create another const, which does not add much value and using the
 * function to get country name, we would need to cater for null checks.
 * Since this is a small list of exceptions, will keep it hardcoded here.
 *
 * @see {@link CountryName}
 */
const CountryLabelOverrides = new Map<CountryName, string>([
  ['Aland_Islands', 'Åland Islands'],
  ['Cabo_Verde', 'Cape Verde'],
  ['Congo_Democratic_Republic', 'Democratic Republic of the Congo'],
  ['Congo_Republic', 'Republic of the Congo'],
  ['Cote_d_Ivoire', `Côte d’Ivoire`],
  ['Korea_North', 'North Korea'],
  ['Korea_South', 'South Korea'],
  ['Lao_Peoples_Democratic_Republic', 'Laos'],
  ['Macao', 'Macau'],
  ['Sao_Tome_and_Principe', 'São Tomé and Príncipe'],
  ['Timor_Leste', 'Timor-Leste'],
  ['Virgin_Islands_British', 'British Virgin Islands'],
]);

/**
 * Returns a user-facing display name for a country.
 *
 * If a label override exists for the given country, it is used.
 * Otherwise, the country name is transformed into title case.
 *
 * This function always returns a string and never `undefined`,
 * as its primary usage is for UI display.
 *
 * @param country - The country name.
 * @returns A display-friendly country name.
 *
 * @see {@link CountryName}
 * @see {@link CountryLabelOverrides}
 */
export function getDisplayNameByCountryName(country: CountryName): string {
  if (CountryLabelOverrides.has(country)) {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return CountryLabelOverrides.get(country)!;
  }

  return toTitleCase(country);
}

/**
 * Returns a user-facing display name for a country based on its code.
 *
 * The provided code is validated and normalised (e.g. `UK` → `GB`)
 * before resolving the corresponding country name.
 *
 * If the code is invalid or cannot be resolved, an empty string is returned.
 * This function always returns a string and never `undefined`.
 *
 * @param code - The country code to resolve.
 * @returns A display-friendly country name, or an empty string if unresolved.
 *
 * @see {@link Code}
 * @see {@link isValidCountryCode}
 * @see {@link normalizeUKtoGB}
 * @see {@link getDisplayNameByCountryName}
 */
export function getDisplayNameByCountryCode(code: Code | string): string {
  if (!isValidCountryCode(code)) return '';

  const normalizedCode = normalizeUKtoGB(code);
  const country = codeToCountry[normalizedCode];
  if (!country) return '';

  return getDisplayNameByCountryName(country);
}

/**
 * Comprehensive country data map indexed by country code.
 * Provides O(1) access to all country-related information.
 *
 * @remarks We deliberately exclude the UK alias (`UK`) from this map
 * to avoid duplication, as it is handled via normalization to `GB`.
 *
 * @see {@link Code}
 * @see {@link CountryInfo}
 * @see {@link normalizeUKtoGB}
 *
 * @example
 * ```ts
 * countryMap['AU'] // { name: 'Australia', code: 'AU', locale: 'en-AU', currency: 'AUD', currencySymbol: '$', phoneCode: '+61' }
 * countryMap['GB'] // { name: 'United_Kingdom', code: 'GB', locale: 'en-GB', currency: 'GBP', currencySymbol: '£', phoneCode: '+44' }
 * ```
 */
export const countryMap = Object.freeze(
  Object.entries(Code).reduce(
    (acc, [countryName, countryCode]) => {
      // Skip UK alias - it's handled by normalization, we only store GB
      if (countryCode === Code.United_Kingdom_UK_Alias) return acc;

      const country = countryName as CountryName;
      const locale = Locale[country];
      const currency = Currency[country];
      const currencySymbol = CurrencySymbol[country];
      const phoneCode = PhoneCode[country];
      const regionalDialingCode = PhoneCodeRegionalCode.has(countryCode)
        ? // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          PhoneCodeRegionalCode.get(countryCode)!
        : [];

      acc[countryCode] = {
        name: getDisplayNameByCountryName(country),
        code: Code[country],
        locale,
        currency,
        currencySymbol,
        phoneCode,
        regionalDialingCode,
      };
      return acc;
    },
    {} as Record<Code, CountryInfo>,
  ),
);

/**
 * Gets the ISO currency code (e.g., 'AUD', 'USD') for a given country code.
 *
 * @param code - The country code (e.g., 'AU', 'GB').
 * @returns The ISO currency code for the country, or undefined if not found.
 * @see {@link Currency}
 * @see {@link isValidCountryCode}
 * @see {@link Code}
 */
export function getCurrencyByCountryCode(code: Code | string): Currency | undefined {
  if (!isValidCountryCode(code)) return undefined;
  const normalizedCode = normalizeUKtoGB(code);
  const country = codeToCountry[normalizedCode];
  if (!country) return undefined;
  return Currency[country];
}

/**
 * Gets the currency symbol (e.g., '$', '€', '¥') for a given country code.
 *
 * @param code - The country code (e.g., 'AU', 'GB').
 * @returns The currency symbol for the country, or undefined if not found.
 * @see {@link CurrencySymbol}
 * @see {@link isValidCountryCode}
 * @see {@link Code}
 */
export function getCurrencySymbolByCountryCode(
  code: Code | string,
): CurrencySymbol | undefined {
  if (!isValidCountryCode(code)) return undefined;
  const normalizedCode = normalizeUKtoGB(code);
  const country = codeToCountry[normalizedCode];
  if (!country) return undefined;
  return CurrencySymbol[country];
}

/**
 * Gets the country name (e.g., 'Australia') for a given country code.
 *
 * @param code - The country code (e.g., 'AU', 'GB').
 * @returns The country name as a string, or undefined if not found.
 * @see {@link CountryName}
 * @see {@link isValidCountryCode}
 * @see {@link Code}
 */
export function getCountryNameByCode(code: Code | string): CountryName | undefined {
  if (!isValidCountryCode(code)) return undefined;
  const normalizedCode = normalizeUKtoGB(code);
  return codeToCountry[normalizedCode];
}

/**
 * Gets the currency symbol for a given locale string (e.g., 'en-AU', 'en-GB').
 *
 * @param locale - The locale string.
 * @returns The currency symbol for the locale's country, or undefined if not found.
 * @see {@link CurrencySymbol}
 * @see {@link Locale}
 */
export function getCurrencySymbolByLocale(
  locale: Locale | string = Locale.Australia,
): CurrencySymbol | undefined {
  const country = localeToCountry[locale];
  if (!country) return undefined;
  return CurrencySymbol[country];
}

/**
 * Gets the ISO currency code (e.g., 'AUD', 'USD') for a given locale string.
 *
 * @param locale - The locale string.
 * @returns The currency code for the locale's country, or undefined if not found.
 */
export function getCurrencyByLocale(
  locale: Locale | string = Locale.Australia,
): Currency | undefined {
  const country = localeToCountry[locale];
  if (!country) return undefined;
  return Currency[country];
}

/**
 * Gets the country code (e.g., 'AU', 'GB') for a given locale string.
 *
 * @param locale - The locale string.
 * @returns The country code, or undefined if not found.
 * @see {@link Locale}
 * @see {@link Code}
 */
export function getCountryCodeByLocale(locale: Locale | string): Code | undefined {
  const country = localeToCountry[locale];
  if (!country) return undefined;
  const code = Code[country];
  return normalizeUKtoGB(code);
}

/**
 * Gets the locale string (e.g., 'en-AU', 'en-GB') for a given country code.
 *
 * @param code - The country code (e.g., 'AU', 'GB').
 * @returns The locale string, or undefined if not found.
 */
export function getLocaleByCountryCode(code: Code | string): Locale | undefined {
  if (!isValidCountryCode(code)) return undefined;
  const normalizedCode = normalizeUKtoGB(code);
  const country = codeToCountry[normalizedCode];
  if (!country) return undefined;
  return Locale[country];
}

/**
 * Gets the international phone code (e.g., '+61', '+44') for a given country code.
 *
 * @param code - The country code (e.g., 'AU', 'GB').
 * @returns The phone code for the country, or undefined if not found.
 * @see {@link PhoneCode}
 * @see {@link isValidCountryCode}
 * @see {@link Code}
 *
 * @example
 * ```ts
 * getPhoneCodeByCountryCode(Code.Australia) // '+61'
 * getPhoneCodeByCountryCode(Code.United_Kingdom) // '+44'
 * getPhoneCodeByCountryCode(Code.United_States) // '+1'
 * getPhoneCodeByCountryCode('XX') // undefined
 * ```
 */
export function getPhoneCodeByCountryCode(code: Code | string): PhoneCode | undefined {
  if (!isValidCountryCode(code)) return undefined;
  const normalizedCode = normalizeUKtoGB(code);
  const country = codeToCountry[normalizedCode];
  if (!country) return undefined;
  return PhoneCode[country];
}

/**
 * Gets the international phone code (e.g., '+61', '+44') for a given locale string.
 *
 * @param locale - The locale string (e.g., 'en-AU', 'en-GB').
 * @returns The phone code for the locale's country, or undefined if not found.
 * @see {@link PhoneCode}
 * @see {@link Locale}
 *
 * @example
 * ```ts
 * getPhoneCodeByLocale(Locale.Australia) // '+61'
 * getPhoneCodeByLocale(Locale.United_Kingdom) // '+44'
 * getPhoneCodeByLocale(Locale.United_States) // '+1'
 * getPhoneCodeByLocale('foo-BAR') // undefined
 * ```
 */
export function getPhoneCodeByLocale(locale: Locale | string): PhoneCode | undefined {
  const country = localeToCountry[locale];
  if (!country) return undefined;
  return PhoneCode[country];
}

/**
 * Gets an array of international phone codes for an array of country codes.
 * Invalid country codes are filtered out (not included in the result).
 *
 * @param codes - Array of country codes (e.g., ['AU', 'GB', 'US']).
 * @returns Array of phone codes for valid countries.
 * @see {@link PhoneCode}
 * @see {@link isValidCountryCode}
 * @see {@link Code}
 *
 * @example
 * ```ts
 * getPhoneCodesByCountryCodes([Code.Australia, Code.United_Kingdom]) // ['+61', '+44']
 * getPhoneCodesByCountryCodes([Code.United_States, Code.Canada]) // ['+1', '+1']
 * getPhoneCodesByCountryCodes([Code.Australia, 'XX', Code.United_Kingdom]) // ['+61', '+44'] (XX filtered out)
 * getPhoneCodesByCountryCodes([]) // []
 * getPhoneCodesByCountryCodes(['XX', 'YY']) // []
 * ```
 */
export function getPhoneCodesByCountryCodes(codes: (Code | string)[]): PhoneCode[] {
  return codes.map(getPhoneCodeByCountryCode).filter((phone) => phone != null);
}

/**
 * Checks if a given string is a valid supported country code.
 * @param code - The country code to validate.
 * @returns `true` if the code is a valid country code from the `Code` enum, `false` otherwise.
 * @see {@link Code}
 */
export function isValidCountryCode(code: string): code is Code {
  return code in codeToCountry;
}

/**
 * Gets complete country information for a given country code.
 *
 * @param code - The country code (e.g., 'AU', 'GB').
 * @returns Country information object, or undefined if not found.
 * @see {@link CountryInfo}
 * @see {@link isValidCountryCode}
 * @see {@link Code}
 *
 * @example
 * ```ts
 * getCountryInfo('AU') // { name: 'Australia', code: 'AU', locale: 'en-AU', currency: 'AUD', currencySymbol: '$', phoneCode: '+61' }
 * getCountryInfo('GB') // { name: 'United_Kingdom', code: 'GB', locale: 'en-GB', currency: 'GBP', currencySymbol: '£', phoneCode: '+44' }
 * getCountryInfo('XX') // undefined
 * ```
 */
export function getCountryInfo(code: Code | string): CountryInfo | undefined {
  if (!isValidCountryCode(code)) return undefined;
  const normalizedCode = normalizeUKtoGB(code);
  return countryMap[normalizedCode];
}

/**
 * Gets an array of all country information objects.
 * Useful for populating dropdowns or lists with complete country data.
 *
 * @returns Array of all country information objects.
 * @see {@link CountryInfo}
 *
 * @example
 * ```ts
 * getAllCountries() // [{ name: 'Algeria', code: 'DZ', ... }, { name: 'Argentina', code: 'AR', ... }, ...]
 * getAllCountries().length // 72
 * ```
 */
export function getAllCountries(): CountryInfo[] {
  return Object.values(countryMap);
}

/**
 * Priority map for phone codes shared by multiple countries.
 * When multiple countries share the same dialing code, this map determines
 * which country should be returned by default.
 *
 * @example
 * - +1 (NANP): Shared by 19 countries/territories → defaults to USA
 * - +44 (UK): Shared by UK, Guernsey, Isle of Man, Jersey → defaults to UK (normalized to GB)
 * - +7: Shared by Russia and Kazakhstan → defaults to Russia
 * - +358: Shared by Finland and Åland Islands → defaults to Finland
 */
const PHONE_CODE_PRIORITY: Record<string, Code> = {
  '+1': Code.United_States,
  '+44': Code.United_Kingdom,
  '+7': Code.Russia,
  '+358': Code.Finland,
};

function normalizePhoneCode(phoneCode: string): string {
  return phoneCode.startsWith('+') ? phoneCode : `+${phoneCode}`;
}

function findCountryByPhoneCode(normalizedPhoneCode: string): Code | undefined {
  const entry = Object.entries(PhoneCode).find(
    ([, code]) => code === normalizedPhoneCode,
  );
  if (!entry) return undefined;

  const [countryName] = entry;
  return Code[countryName as keyof typeof Code];
}

function getPreferredCountryForPhoneCode(normalizedPhoneCode: string): Code | undefined {
  return PHONE_CODE_PRIORITY[normalizedPhoneCode];
}

/**
 * Retrieves the ISO country code (enum `Code`) corresponding to a given telephone dialing code.
 *
 * This function handles the normalization of the input phone code (ensuring it starts with a '+')
 * and performs a reverse lookup on the `PhoneCode` enumeration. For phone codes shared by
 * multiple countries (e.g., +1 for US/CA, +44 for UK/territories), it returns the most
 * commonly used country as defined in the priority map.
 *
 * It also handles specific edge cases, such as normalizing 'UK' codes to 'GB'.
 *
 * @param phoneCode - The telephone dialing code (e.g., "+1", "44", "+61") to look up.
 * @returns The corresponding country `Code` if found, strictly typed as the `Code` enum, or `undefined` if no match is found.
 * @remarks This function uses a naive reverse lookup approach. For shared phone codes, it defaults
 * to the most common country (US for +1, GB for +44, etc.). This is mostly to handle basic reset
 * on PhoneField and should be used with caution.
 *
 * @example
 * ```ts
 * getCountryCodeByPhoneCode('+1') // Code.United_States (not Canada)
 * getCountryCodeByPhoneCode('+44') // Code.United_Kingdom
 * getCountryCodeByPhoneCode('+61') // Code.Australia
 * ```
 */
export function getCountryCodeByPhoneCode(phoneCode: string): Code | undefined {
  const normalizedPhoneCode = normalizePhoneCode(phoneCode);
  const preferredCountry = getPreferredCountryForPhoneCode(normalizedPhoneCode);
  const countryCode = preferredCountry ?? findCountryByPhoneCode(normalizedPhoneCode);

  return countryCode ? normalizeUKtoGB(countryCode) : undefined;
}
